import { SelectionLike } from './types';

/**
 * Maps a stored selection to the shape read APIs (`list`/`filter`/`get`) should return.
 * Apps inject app-specific normalisation here (e.g. folding transient state into the record);
 * when omitted the stored selection is returned as-is.
 */
export type SelectionConverter<TSelection, TView> = (selection: TSelection) => TView;

const identity = <TSelection, TView>(selection: TSelection): TView => selection as unknown as TView;

/**
 * In-memory, framework-agnostic manager for a project's list of selections. Holds no storage:
 * mutating methods return the updated array so the caller can persist it however it likes.
 */
export class SelectionRepository<TSelection extends SelectionLike, TView = TSelection> {
    private selections: TSelection[];

    private readonly convert: SelectionConverter<TSelection, TView>;

    public constructor(
        selections: TSelection[],
        convert: SelectionConverter<TSelection, TView> = identity,
    ) {
        this.selections = selections;
        this.convert = convert;
    }

    public proposeName(prefix = 'Selection'): string {
        let candidate = this.selections.length + 1;
        let found = this.selections.find((s) => s.name === `${prefix} ${candidate}`);
        while (found !== undefined) {
            candidate += 1;
            const name = `${prefix} ${candidate}`;
            found = this.selections.find((s) => s.name === name);
        }
        return `${prefix} ${candidate}`;
    }

    public count(): number {
        return this.selections.length;
    }

    public list(limit: number, offset: number): TView[] {
        return this.selections.slice(offset, offset + limit).map((s) => this.convert(s));
    }

    public filter(type: TSelection['type']): TView[] {
        return this.selections.filter((item) => item.type.includes(type)).map((s) => this.convert(s));
    }

    public remove(selection: TSelection): TSelection[] {
        this.selections = this.selections.filter((s) => s.name !== selection.name);
        return this.selections;
    }

    public add(selection: TSelection): TSelection[] {
        this.selections = [selection, ...this.selections];
        return this.selections;
    }

    public get(name: string): TView | null {
        const item = this.selections.find((v) => v.name === name);
        return item ? this.convert(item) : null;
    }

    public update(selection: TSelection): boolean {
        const item = this.selections.findIndex((v) => v.name === selection.name);
        if (item > -1) {
            this.selections[item] = selection;
        } else {
            this.add(selection);
        }
        return true;
    }

    public rename(selection: TSelection, name: string): TSelection[] {
        const item = this.selections.findIndex((v) => v.name === selection.name);
        if (item > -1) {
            this.selections[item] = { ...selection, name } as TSelection;
        }
        return this.selections;
    }

    /**
     * Reorder the selections named in `orderedNames` into that order, leaving every
     * other selection in its original slot. Used to persist a drag-and-drop reorder
     * of a single (paginated) page without disturbing items outside it. Names not
     * present are ignored; the raw selections are kept (so app-specific state survives).
     */
    public reorderByNames(orderedNames: string[]): TSelection[] {
        const reordered = orderedNames
            .map((name) => this.selections.find((s) => s.name === name))
            .filter((s): s is TSelection => s !== undefined);

        // Walk the original list, refilling the slots held by the reordered subset in
        // their new order and leaving every other selection untouched.
        this.selections = this.selections.reduce<{ items: TSelection[]; cursor: number }>(
            (acc, selection) => {
                if (orderedNames.includes(selection.name)) {
                    acc.items.push(reordered[acc.cursor]);
                    acc.cursor += 1;
                } else {
                    acc.items.push(selection);
                }
                return acc;
            },
            { items: [], cursor: 0 },
        ).items;
        return this.selections;
    }

    public duplicate(selection: TSelection): TSelection[] {
        let candidate = 1;
        let found = this.selections.find((s) => s.name === `${selection.name} (${candidate})`);
        while (found !== undefined) {
            candidate += 1;
            const name = `${selection.name} (${candidate})`;
            found = this.selections.find((s) => s.name === name);
        }
        const name = `${selection.name} (${candidate})`;
        return this.add({
            ...selection,
            name,
        } as TSelection);
    }
}
